import { useCallback, useState } from 'react';
import { useApi } from '../api';
import { BulkTranslateResult } from '../components/products/types';

interface UseBulkTranslateReturn {
  bulkTranslate: (productIds: string[], targetLanguage: string) => Promise<BulkTranslateResult | null>;
  loading: boolean;
  error: string | null;
  result: BulkTranslateResult | null;
  reset: () => void;
}

export const useBulkTranslate = (): UseBulkTranslateReturn => {
  const api = useApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BulkTranslateResult | null>(null);

  const bulkTranslate = useCallback(
    async (productIds: string[], targetLanguage: string): Promise<BulkTranslateResult | null> => {
      setLoading(true);
      setError(null);
      setResult(null);
      try {
        const data = await api.post('/translations/bulk-translate', {
          product_ids: productIds,
          target_language: targetLanguage,
        });
        setResult(data as BulkTranslateResult);
        return data as BulkTranslateResult;
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'An error occurred during bulk translation.';
        setError(message);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  const reset = useCallback(() => {
    setError(null);
    setResult(null);
  }, []);

  return { bulkTranslate, loading, error, result, reset };
};
